/**
 * WHERE THIS FEATURE CAN SEND SOMEBODY, and the one seam the router is plugged into.
 *
 * ADR-0007 rules TanStack Router for both apps. When this file was written `@tanstack/react-router`
 * was not installed, so there was no `navigate`, and a screen that has no way to move the reader on
 * is a dead end. So navigation became a single injected function with a real default.
 *
 * The router has since landed, and the app's root route binds this seam to it
 * (`features/shell/RouterNavigateProvider.tsx`), so in the app every call below moves in place.
 *
 * THE ROUTE PATHS ARE OURS AND ARE NOT RULED. `adr/0007-web-app-stack.md` fixes only `/enter`
 * (the hand-off landing) and the magic-link handler; everything else is layout and navigation,
 * which decision-log row 99 as amended by row 117 puts squarely in this track. `/support` and
 * `/terms` follow the UI track's own proposal (docs/ui/04-pages.md, :81). No owner question is
 * manufactured out of them.
 */
import { createContext, useContext } from 'react';

export const CABINET_ROUTES = {
  support: '/support',
  /** The terms gate and the one-time cutover interstitial. */
  terms: '/terms',
  /** Where an author's real money figures live — the Payments page reads the live policy. */
  payments: '/payments',
  /** The application wizard, for a caller who is not an approved author yet. */
  application: '/apply',
  /** Cold-start sign-in; also the magic-link handler. Ruled at adr/0007. */
  signIn: '/sign-in',
} as const;

export type CabinetRoute = (typeof CABINET_ROUTES)[keyof typeof CABINET_ROUTES];

export type Navigate = (to: CabinetRoute) => void;

/**
 * The default is a full-page navigation. It is correct — the cabinet owns the whole of
 * `authors.moonx.app` and its Caddy fragment serves `try_files {path} /index.html`, so any of these
 * paths loads the SPA — it is only slower than a client-side transition.
 */
const hardNavigate: Navigate = (to) => {
  window.location.assign(to);
};

export const NavigateContext = createContext<Navigate>(hardNavigate);

export function useNavigate(): Navigate {
  return useContext(NavigateContext);
}
